// Pure formatting helpers for the meeting analysis matrix / historical
// performance views. Ported unchanged from docker/admin/src/lib/history-performance.ts
// — kept free of React/DOM so they unit-test in isolation. ROI values arrive
// from the API already expressed as a percentage (e.g. -100, +50) on a $10
// flat-stake SP basis.

/** Whole-number win percentage from a winners/races pair. */
export function winPercentage(
  winners: number | string | null | undefined,
  races: number | string | null | undefined
): number {
  const w = Number(winners);
  const r = Number(races);
  if (!r || r <= 0 || Number.isNaN(w)) return 0;
  return Math.round((w / r) * 100);
}

/** Tailwind text colour for a win-percentage cell. */
export function winPercentageClass(pct: number): string {
  if (pct >= 50) return 'text-emerald-600';
  if (pct >= 30) return 'text-amber-600';
  return 'text-red-500';
}

export interface RoiDisplay {
  text: string;
  className: string;
}

/**
 * Format an ROI percentage for display: signed, one decimal place, with a
 * red/green colour. Null (no settled bets of that type) renders as a dash.
 */
export function formatRoi(roi: number | string | null | undefined): RoiDisplay {
  // Postgres NUMERIC/ROUND() values arrive over the wire as strings, so coerce
  // before any numeric formatting.
  const value = roi == null ? NaN : Number(roi);
  if (Number.isNaN(value)) {
    return { text: '—', className: 'text-muted-foreground' };
  }
  const sign = value > 0 ? '+' : '';
  return {
    text: `${sign}${value.toFixed(1)}%`,
    className: value >= 0 ? 'text-emerald-600' : 'text-red-500',
  };
}

/** Human label for an analysis type, e.g. "T1 (Primary)", "T3", "T5 (Auto Review)". */
export function analysisTypeLabel(typeId: number): string {
  const suffix = typeId === 1 ? ' (Primary)' : typeId === 2 ? ' (Secondary)' : typeId === 5 ? ' (Auto Review)' : '';
  return `T${typeId}${suffix}`;
}

// ── Race-model matrix helpers ──────────────────────────────────────────────

export type RaceMatrixCellStatus = 'green' | 'yellow' | 'red' | 'none';

export interface RaceMatrixCell {
  status: RaceMatrixCellStatus;
  top1Pos: number | null;
  // Model that produced this race's analysis for this slot. Carried per cell
  // (not just per slot) because single races are routinely swapped between
  // analysis types, so one slot row can mix several models across races.
  aiModel: string | null;
  aiVersion: string | null;
}

export interface RaceMatrixModelTally {
  aiModel: string | null;
  aiVersion: string | null;
  races: number; // number of races this model filled in the slot
}

export interface RaceMatrixSlot {
  analysisTypeId: number;
  aiModel: string | null;
  aiVersion: string | null;
  // Distinct models used across the slot's races (most-used first). One entry
  // means the slot is uniform; more than one means it has been part-swapped.
  models: RaceMatrixModelTally[];
  cells: Record<number, RaceMatrixCell>;
  topPickWinners: number;
  totalWinners: number;
  topPickRoi: number | null;
  valueRoi: number | null;
}

export interface RaceMatrixData {
  meetingId: string;
  races: number[];
  slots: RaceMatrixSlot[];
}

/** Tailwind background class for a matrix cell. */
export function raceMatrixCellClass(status: RaceMatrixCellStatus): string {
  switch (status) {
    case 'green':
      return 'bg-emerald-500';
    case 'yellow':
      return 'bg-yellow-400';
    case 'red':
      return 'bg-red-400';
    default:
      return 'bg-muted';
  }
}

/** Readable text colour for a model code sitting on a coloured matrix cell. */
export function raceMatrixCellTextClass(status: RaceMatrixCellStatus): string {
  switch (status) {
    case 'green':
      return 'text-white';
    case 'red':
      return 'text-white';
    case 'yellow':
      return 'text-gray-900';
    default:
      return 'text-muted-foreground';
  }
}

/** Full model label, e.g. "Claude Opus 4.6". Empty parts are dropped. */
export function modelLabel(aiModel: string | null | undefined, aiVersion: string | null | undefined): string {
  const parts = [aiModel, aiVersion].map((p) => (p ?? '').trim()).filter(Boolean);
  return parts.length ? parts.join(' ') : '—';
}

/**
 * Compact 2-char model code for a matrix cell. The Claude family is told apart
 * by its version (Opus/Sonnet/Haiku); other providers by the model name.
 * e.g. Claude+Opus → "OP", Claude+Sonnet → "SO", Grok → "GR", Deepseek → "DE".
 */
export function modelShortCode(aiModel: string | null | undefined, aiVersion: string | null | undefined): string {
  const m = (aiModel ?? '').trim();
  const v = (aiVersion ?? '').trim();
  if (!m && !v) return '—';
  if (/claude/i.test(m) && v) return v.slice(0, 2).toUpperCase();
  if (m) return m.slice(0, 2).toUpperCase();
  return v.slice(0, 2).toUpperCase();
}

/** Per-analysis-type badge + row tint: the four model slots and auto review (5). */
export function analysisTypeStyle(typeId: number): { badge: string; row: string } {
  switch (typeId) {
    case 1:
      return { badge: 'bg-violet-100 text-violet-700 dark:bg-violet-950/40 dark:text-violet-400', row: 'bg-violet-50/40 dark:bg-violet-950/10' };
    case 2:
      return { badge: 'bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-400', row: 'bg-sky-50/40 dark:bg-sky-950/10' };
    case 3:
      return { badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400', row: 'bg-emerald-50/40 dark:bg-emerald-950/10' };
    case 4:
      return { badge: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400', row: 'bg-amber-50/40 dark:bg-amber-950/10' };
    case 5:
      return { badge: 'bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400', row: 'bg-rose-50/40 dark:bg-rose-950/10' };
    default:
      return { badge: 'bg-muted text-muted-foreground', row: '' };
  }
}

export interface AnalysisModelInfo {
  analysis_type_id: number;
  ai_model: string | null;
  ai_version: string | null;
  race_count?: number | null;
  total_races?: number | null;
  latest_analysis_at?: string | null;
  scratched_tips?: number | null;
}

/** Normalise the /analysis-models response into a sorted, per-type list. */
export function toAnalysisModel(rows: AnalysisModelInfo[]): AnalysisModelInfo[] {
  return (rows ?? [])
    .map((r) => ({
      analysis_type_id: r.analysis_type_id,
      ai_model: r.ai_model ?? null,
      ai_version: r.ai_version ?? null,
      race_count: r.race_count ?? null,
      total_races: r.total_races ?? null,
      latest_analysis_at: r.latest_analysis_at ?? null,
      scratched_tips: r.scratched_tips ?? null,
    }))
    .sort((a, b) => a.analysis_type_id - b.analysis_type_id);
}
